import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  CloudSync as SyncIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Visibility as PreviewIcon,
  CloudUpload as UploadIcon,
  Business as BusinessIcon,
  AccountTree as AccountTreeIcon,
  Timeline as TimelineIcon,
  LibraryBooks as LibraryIcon
} from '@mui/icons-material';
import { useSync } from '../hooks/useSync';
import api from '../api/axios';

const SyncDialog = ({ 
  open, 
  onClose, 
  syncType = 'company', // 'company', 'project', 'flowsteps' 
  targetId,
  targetName = '',
  onSyncComplete 
}) => {
  const { 
    syncStatus, 
    initializeSync, 
    compareData, 
    syncCompany, 
    syncProject, 
    syncFlowSteps,
    closeSync 
  } = useSync();

  const [activeStep, setActiveStep] = useState(0);
  const [stagingUri, setStagingUri] = useState('');
  const [productionUri, setProductionUri] = useState('');
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [syncOptions, setSyncOptions] = useState({
    dryRun: false,
    overwriteExisting: true,
    syncDependencies: true
  });
  const [syncResult, setSyncResult] = useState(null);
  const [hasCalledComplete, setHasCalledComplete] = useState(false);

  // Debug sync result changes
  useEffect(() => {
    console.log('🔄 [SYNC DIALOG] syncResult changed:', syncResult);
  }, [syncResult]);

  // Debug active step changes
  useEffect(() => {
    console.log('🔄 [SYNC DIALOG] activeStep changed:', activeStep);
  }, [activeStep]);

  const steps = [
    'Configure Databases',
    'Compare Data',
    'Review Changes',
    'Execute Sync'
  ];

  useEffect(() => {
    console.log('🔄 [SYNC DIALOG] useEffect triggered, open:', open);
    if (open) {
      console.log('📂 [SYNC DIALOG] Modal opened, loading database config...');
      loadDatabaseConfig();
    } else {
      console.log('❌ [SYNC DIALOG] Modal closed, resetting state...');
      setActiveStep(0);
      setComparisonData(null);
      setSyncResult(null);
      setHasCalledComplete(false);
    }
  }, [open]);

  const loadDatabaseConfig = async () => {
    setLoadingConfig(true);
    try {
      const response = await api.get('/sync/config');
      setStagingUri(response.data.stagingUri || 'mongodb://localhost:27017/neoconverter_staging');
      setProductionUri(response.data.productionUri || '');
    } catch (error) {
      console.error('Failed to load database config:', error);
      // Set default values if API fails
      setStagingUri('mongodb://localhost:27017/neoconverter_staging');
      setProductionUri('');
    }
    setLoadingConfig(false);
  };

  const handleInitialize = async () => {
    try {
      await initializeSync(stagingUri, productionUri);
      setActiveStep(1);
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleCompare = async () => {
    try {
      const data = await compareData();
      setComparisonData(data);
      setActiveStep(2);
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleSync = async () => {
    console.log('🚀 [SYNC DIALOG] Starting sync process...', { syncType, targetId, syncOptions });
    try {
      let result;
      
      switch (syncType) {
        case 'company':
          console.log('📊 [SYNC DIALOG] Syncing company...');
          result = await syncCompany(targetId, syncOptions);
          break;
        case 'project':
          console.log('📊 [SYNC DIALOG] Syncing project...');
          result = await syncProject(targetId, syncOptions);
          break;
        case 'flowsteps':
          console.log('📊 [SYNC DIALOG] Syncing flow steps...');
          result = await syncFlowSteps(targetId, syncOptions);
          break;
        default:
          throw new Error('Invalid sync type');
      }
      
      console.log('✅ [SYNC DIALOG] Sync completed successfully:', result);
      setSyncResult(result);
      console.log('🔄 [SYNC DIALOG] Setting active step to 3');
      setActiveStep(3);
      
      // Don't call onSyncComplete immediately to prevent modal reset
      // It will be called when the modal is closed
      console.log('⏳ [SYNC DIALOG] Delaying onSyncComplete callback until modal closes');
    } catch (error) {
      console.error('❌ [SYNC DIALOG] Sync failed:', error);
      // Error handled in hook
    }
  };

  const handleClose = async () => {
    // Call onSyncComplete if we have a result and haven't called it yet
    if (syncResult && onSyncComplete && !hasCalledComplete) {
      console.log('📞 [SYNC DIALOG] Calling onSyncComplete callback on modal close');
      onSyncComplete(syncResult);
      setHasCalledComplete(true);
    }
    
    await closeSync();
    onClose();
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Configure your database connections to sync {syncType} data from staging to production.
            </Typography>
            
            {loadingConfig ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography variant="body2">Loading database configuration...</Typography>
              </Box>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Staging Database URI"
                  value={stagingUri}
                  onChange={(e) => setStagingUri(e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="MongoDB connection string for your staging/local database"
                />
                
                <TextField
                  fullWidth
                  label="Production Database URI"
                  value={productionUri}
                  onChange={(e) => setProductionUri(e.target.value)}
                  sx={{ mb: 3 }}
                  helperText="MongoDB connection string for your production database"
                />
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Ensure both database URIs are accessible and you have appropriate permissions.
                  </Typography>
                </Alert>
              </>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Compare data between staging and production to see what will be synced.
            </Typography>
            
            {!comparisonData ? (
              <Box textAlign="center" py={3}>
                <Button
                  variant="contained"
                  startIcon={syncStatus.isSyncing ? <CircularProgress size={20} /> : <RefreshIcon />}
                  onClick={handleCompare}
                  disabled={syncStatus.isSyncing}
                >
                  {syncStatus.isSyncing ? 'Comparing...' : 'Compare Data'}
                </Button>
              </Box>
            ) : (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Comparison Results
                  </Typography>
                  
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <InfoIcon color="info" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Total Items in Staging"
                        secondary={comparisonData.stagingCount || 0}
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <InfoIcon color="info" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Total Items in Production"
                        secondary={comparisonData.productionCount || 0}
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <UploadIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Items to Sync"
                        secondary={comparisonData.itemsToSync || 0}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Review sync options and preview changes before executing.
            </Typography>
            
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sync Target
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Chip 
                    label={syncType} 
                    color="primary" 
                    variant="outlined" 
                  />
                  <Typography variant="body1">
                    {targetName || targetId}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
            
            {comparisonData && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Data to be Synced
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Based on the comparison, the following items will be synced:
                  </Typography>
                  <List dense>
                    {comparisonData.summary?.companies && (
                      <ListItem>
                        <ListItemIcon>
                          <BusinessIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${comparisonData.summary.companies.total || 0} Companies`}
                          secondary={`${comparisonData.summary.companies.created || 0} new, ${comparisonData.summary.companies.updated || 0} updated`}
                        />
                      </ListItem>
                    )}
                    {comparisonData.summary?.projects && (
                      <ListItem>
                        <ListItemIcon>
                          <AccountTreeIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${comparisonData.summary.projects.total || 0} Projects`}
                          secondary={`${comparisonData.summary.projects.created || 0} new, ${comparisonData.summary.projects.updated || 0} updated`}
                        />
                      </ListItem>
                    )}
                    {comparisonData.summary?.flowSteps && (
                      <ListItem>
                        <ListItemIcon>
                          <TimelineIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${comparisonData.summary.flowSteps.total || 0} Flow Steps`}
                          secondary={`${comparisonData.summary.flowSteps.created || 0} new, ${comparisonData.summary.flowSteps.updated || 0} updated`}
                        />
                      </ListItem>
                    )}
                    {comparisonData.summary?.matchingLibraries && comparisonData.summary.matchingLibraries.total > 0 && (
                      <ListItem>
                        <ListItemIcon>
                          <LibraryIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${comparisonData.summary.matchingLibraries.total || 0} Matching Libraries`}
                          secondary={`${comparisonData.summary.matchingLibraries.created || 0} new, ${comparisonData.summary.matchingLibraries.updated || 0} updated`}
                        />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            )}
            
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sync Options
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={syncOptions.dryRun}
                      onChange={(e) => setSyncOptions(prev => ({
                        ...prev,
                        dryRun: e.target.checked
                      }))}
                    />
                  }
                  label="Dry Run (Preview only)"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={syncOptions.overwriteExisting}
                      onChange={(e) => setSyncOptions(prev => ({
                        ...prev,
                        overwriteExisting: e.target.checked
                      }))}
                    />
                  }
                  label="Overwrite Existing Data"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={syncOptions.syncDependencies}
                      onChange={(e) => setSyncOptions(prev => ({
                        ...prev,
                        syncDependencies: e.target.checked
                      }))}
                    />
                  }
                  label="Sync Dependencies"
                />
              </CardContent>
            </Card>
            
            {syncOptions.dryRun && (
              <Alert severity="info">
                This will be a dry run - no actual changes will be made to production.
              </Alert>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            {!syncResult ? (
              <Box textAlign="center" py={3}>
                <Button
                  variant="contained"
                  startIcon={syncStatus.isSyncing ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                  onClick={handleSync}
                  disabled={syncStatus.isSyncing}
                  size="large"
                >
                  {syncStatus.isSyncing ? 'Syncing...' : `Sync ${syncType}`}
                </Button>
              </Box>
            ) : (
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <CheckIcon color="success" sx={{ fontSize: 32 }} />
                    <Typography variant="h5" color="success.main" fontWeight="bold">
                      Sync Completed Successfully! 🎉
                    </Typography>
                  </Box>
                  
                  <Alert severity="success" sx={{ mb: 3 }}>
                    <Typography variant="body1">
                      Your {syncType} has been successfully synced to production.
                    </Typography>
                  </Alert>
                  
                  <Typography variant="h6" gutterBottom>
                    Sync Summary
                  </Typography>
                  
                  <List dense>
                    {syncResult.results && (
                      <>
                        {syncResult.results.companies && (
                          <ListItem>
                            <ListItemIcon>
                              <BusinessIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Companies"
                              secondary={`${syncResult.results.companies.created || 0} created, ${syncResult.results.companies.updated || 0} updated`}
                            />
                          </ListItem>
                        )}
                        {syncResult.results.projects && (
                          <ListItem>
                            <ListItemIcon>
                              <AccountTreeIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Projects"
                              secondary={`${syncResult.results.projects.created || 0} created, ${syncResult.results.projects.updated || 0} updated`}
                            />
                          </ListItem>
                        )}
                        {syncResult.results.flowSteps && (
                          <ListItem>
                            <ListItemIcon>
                              <TimelineIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Flow Steps"
                              secondary={`${syncResult.results.flowSteps.created || 0} created, ${syncResult.results.flowSteps.updated || 0} updated`}
                            />
                          </ListItem>
                        )}
                        {syncResult.results.matchingLibraries && (
                          <ListItem>
                            <ListItemIcon>
                              <LibraryIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Matching Libraries"
                              secondary={`${syncResult.results.matchingLibraries.created || 0} libraries created, ${syncResult.results.matchingLibraries.updated || 0} libraries updated, ${syncResult.results.matchingLibraries.entries?.created || 0}/${syncResult.results.matchingLibraries.entries?.updated || 0}/${syncResult.results.matchingLibraries.entries?.deleted || 0} entries (C/U/D), ${syncResult.results.matchingLibraries.sources?.created || 0}/${syncResult.results.matchingLibraries.sources?.updated || 0}/${syncResult.results.matchingLibraries.sources?.deleted || 0} sources (C/U/D)`}
                            />
                          </ListItem>
                        )}
                      </>
                    )}
                    
                    {syncResult.results && syncResult.results.errors && syncResult.results.errors.length > 0 && (
                      <ListItem>
                        <ListItemIcon>
                          <WarningIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Warnings"
                          secondary={`${syncResult.results.errors.length} warnings encountered`}
                        />
                      </ListItem>
                    )}
                  </List>
                  
                  {syncResult.results && syncResult.results.errors && syncResult.results.errors.length > 0 && (
                    <Accordion sx={{ mt: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>View Warnings</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List dense>
                          {syncResult.results.errors.map((error, index) => (
                            <ListItem key={index}>
                              <ListItemIcon>
                                <WarningIcon color="warning" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={error.companyName || error.projectName || error.stepName || `Error ${index + 1}`}
                                secondary={error.error}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )}
                  
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      You can now close this dialog or sync another item.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return stagingUri && productionUri && !loadingConfig;
      case 1:
        return comparisonData !== null;
      case 2:
        return true;
      default:
        return false;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <SyncIcon />
            <Typography variant="h6">
              Sync {syncType} to Production
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {syncStatus.error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {syncStatus.error}
          </Alert>
        )}
        
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {getStepContent(index)}
                
                {index < steps.length - 1 && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (index === 0) {
                          handleInitialize();
                        } else if (index === 1) {
                          setActiveStep(2);
                        } else if (index === 2) {
                          setActiveStep(3);
                        }
                      }}
                      disabled={!canProceed() || syncStatus.isSyncing}
                      sx={{ mr: 1 }}
                    >
                      {index === 0 ? 'Connect' : 'Next'}
                    </Button>
                    
                    {index > 0 && (
                      <Button onClick={() => setActiveStep(index - 1)}>
                        Back
                      </Button>
                    )}
                  </Box>
                )}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SyncDialog;
